import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Play, Newspaper, Handshake } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { cms } from '../i18n/localize';

type MediaType = 'video' | 'article' | 'partner';

interface MediaCoverageItem {
  id?: string | number;
  title: string;
  description?: string;
  date?: string;
  type?: MediaType;
  videoUrl?: string;
  imageUrl?: string;
  externalUrl?: string;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const typeConfig: Record<MediaType, { labelKey: string; icon: React.ElementType }> = {
  video: { labelKey: 'content:media.typeVideo', icon: Play },
  article: { labelKey: 'content:media.typeArticle', icon: Newspaper },
  partner: { labelKey: 'content:media.typePartner', icon: Handshake },
};

function getVideoEmbedUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    // youtu.be/VIDEO_ID
    if (parsed.hostname === 'youtu.be') {
      const id = parsed.pathname.slice(1).split('/')[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    // youtube.com/watch?v=VIDEO_ID
    if (/^(www\.)?youtube\.com$/i.test(parsed.hostname)) {
      if (parsed.pathname.startsWith('/embed/')) {
        return url;
      }
      const id = parsed.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch {
    // ignore invalid URL
  }
  return url;
}

const MediaCoverageSection: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<MediaCoverageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState<Record<string | number, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const fetchMedia = async () => {
      try {
        const res = await apiFetch('/api/public/media-coverage');
        if (!res.ok) throw new Error('Failed to fetch media coverage');
        const json = await res.json();
        if (cancelled) return;
        const list: MediaCoverageItem[] = Array.isArray(json) ? json : json.data ?? json.items ?? [];
        setItems(list);
      } catch {
        if (cancelled) return;
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMedia();
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizeType = (type?: string): MediaType => {
    if (type === 'video' || type === 'article' || type === 'partner') return type;
    if (type?.includes('video') || type?.includes('影片')) return 'video';
    if (type?.includes('partner') || type?.includes('合作') || type?.includes('伙伴')) return 'partner';
    return 'article';
  };

  if (loading) {
    return (
      <section id="media-coverage" className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-stone-100 animate-pulse"
              >
                <div className="aspect-video bg-stone-200" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-stone-200 rounded w-3/4" />
                  <div className="h-4 bg-stone-200 rounded w-full" />
                  <div className="h-4 bg-stone-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section id="media-coverage" className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
              {t('content:media.badge')}
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
              {t('content:media.heading')}
            </h2>
            <p className="text-stone-500">{t('content:media.empty')}</p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="media-coverage" className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
            {t('content:media.badge')}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
            {t('content:media.heading')}
          </h2>
          <p className="text-stone-600 leading-relaxed">
            {t('content:media.intro')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item, idx) => {
            const id = item.id ?? idx;
            const type = normalizeType(item.type);
            const { labelKey, icon: TypeIcon } = typeConfig[type];
            const embedUrl = getVideoEmbedUrl(item.videoUrl);
            const isVideo = type === 'video' && embedUrl;
            const externalUrl = item.externalUrl;

            return (
              <motion.div
                key={id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, delay: (idx % 2) * 0.1, ease: 'easeOut' },
                  },
                }}
                className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:shadow-lg transition-shadow"
              >
                <div className="relative bg-stone-100">
                  {isVideo ? (
                    <div className="aspect-video">
                      <iframe
                        src={embedUrl}
                        title={item.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video relative overflow-hidden">
                      {!imageLoaded[id] && (
                        <div className="absolute inset-0 animate-pulse bg-stone-200" />
                      )}
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        onLoad={() =>
                          setImageLoaded((prev) => ({ ...prev, [id]: true }))
                        }
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          setImageLoaded((prev) => ({ ...prev, [id]: true }));
                        }}
                      />
                    </div>
                  )}

                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-3 py-1 bg-white/90 backdrop-blur-sm text-stone-700 text-xs font-bold rounded-full shadow-sm">
                    <TypeIcon size={12} />
                    {t(labelKey)}
                  </span>
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-stone-800 mb-2">{cms(`mediaItems.${id}.title`, item.title)}</h3>
                  {item.description && (
                    <p className="text-stone-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {cms(`mediaItems.${id}.description`, item.description)}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    {item.date && (
                      <span className="text-xs text-stone-400">{cms(`mediaItems.${id}.date`, item.date)}</span>
                    )}
                    {externalUrl && (
                      <a
                        href={externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-bold text-[#C2410C] hover:text-[#9a3412] transition-colors"
                      >
                        {t('content:media.readMore')}
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MediaCoverageSection;
